import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the one-click-too-many start screen. Three
 * short slides (what/who, main risks, key terms) shown in the reader while the
 * 3D room downloads. Content only; the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. accidental leak: prompt, confidential file, outside service, link */
/* ------------------------------------------------------------------ */
function LeakArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: AI-built dashboard")} aria={tx("An AI dashboard builder that is not approved, a confidential customer file uploaded to an outside service, and a public link anyone can read")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Dashboard builder")}
      </text>
      <rect className="sr-anim-pulse" x="238" y="38" width="106" height="22" rx="11" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <text x="291" y="53" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle" letterSpacing="0.6">
        {tx("NOT APPROVED")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <rect x="16.5" y="80.5" width="327" height="30" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="29" y="99.5" fontSize="12.5" fill={C.text}>
        {tx("Build me a sales dashboard")}
      </text>
      <rect className="sr-anim-blink" x="190" y="89" width="1.5" height="14" rx="0.75" fill={C.mint} />

      <rect x="16.5" y="122.5" width="327" height="44" rx="8" fill={C.code} stroke={C.line} />
      <rect x="28" y="133" width="18" height="22" rx="3" fill="none" stroke={C.soft} strokeWidth="1.4" />
      <line x1="32" y1="140" x2="42" y2="140" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="32" y1="145" x2="42" y2="145" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="32" y1="150" x2="38" y2="150" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round" />
      <text x="56" y="141" fontSize="11.5" fontFamily={MONO} fill={C.text}>
        Customer_list.xlsx
      </text>
      <text x="56" y="157" fontSize="10.5" fill={C.muted}>
        {tx("Real clients, prices, contacts")}
      </text>
      <rect x="258" y="136" width="76" height="18" rx="9" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.45" />
      <text x="296" y="148.5" fontSize="9.5" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.5">
        {tx("CONFIDENTIAL")}
      </text>

      <line x1="180" y1="170" x2="180" y2="184" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" />
      <polyline points="175.5,180 180,185 184.5,180" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <text x="192" y="181" fontSize="10.5" fill={C.muted}>
        {tx("uploaded")}
      </text>

      <rect x="16.5" y="190.5" width="327" height="40" rx="8" fill={C.panel2} stroke={C.line} />
      <path d="M40 216 h22 a6 6 0 0 0 0 -12 a8 8 0 0 0 -15 -2 a7 7 0 0 0 -7 14 z" fill="none" stroke={C.amber} strokeWidth="1.5" strokeLinejoin="round" />
      <text x="78" y="207" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Outside service")}
      </text>
      <text x="78" y="222" fontSize="10.5" fill={C.muted}>
        {tx("No agreement with your company")}
      </text>

      <rect x="16.5" y="242.5" width="327" height="41" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="34" cy="263" r="5.5" fill="none" stroke={C.red} strokeWidth="1.5" />
      <circle cx="34" cy="263" r="1.8" fill={C.red} />
      <text x="48" y="259" fontSize="12" fontWeight="700" fill={C.red}>
        {tx("Public link published")}
      </text>
      <text x="48" y="274" fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        {tx("Anyone with the link can read it")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. consent screen: permission rows, risk tags, Accept with a ring    */
/* ------------------------------------------------------------------ */
interface PermSpec {
  label: string;
  level: "HIGH" | "LOW";
}

const PERMS: PermSpec[] = [
  { label: "Read all your email", level: "HIGH" },
  { label: "Read every file you can open", level: "HIGH" },
  { label: "Send email as you", level: "HIGH" },
  { label: "Sign you in", level: "LOW" },
];

function ConsentArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label={tx("Example: permission request")} aria={tx("A permission request screen from an unknown app asking to read email, read files and send email as you, with Report, Cancel and Accept buttons")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Permissions requested")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <rect x="16.5" y="80.5" width="327" height="34" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="29" y="95" fontSize="12" fontWeight="700" fill={C.text}>
        {tx("Live Data Sync")}
      </text>
      <text x="29" y="108" fontSize="10" fontFamily={MONO} fill={C.muted}>
        {tx("publisher: unverified")}
      </text>
      <rect x="244" y="89" width="88" height="18" rx="9" fill={C.mint} fillOpacity="0.12" stroke={C.mint} strokeOpacity="0.4" />
      <text x="288" y="101.5" fontSize="9.5" fontWeight="700" fill={C.mint} textAnchor="middle" letterSpacing="0.4">
        {tx("SIGN-IN IS REAL")}
      </text>

      <text x="16" y="133" fontSize="10.5" fill={C.muted}>
        {tx("This app wants to:")}
      </text>
      {PERMS.map((p, i) => {
        const y = 140 + i * 28;
        const high = p.level === "HIGH";
        const col = high ? C.red : C.mint;
        return (
          <g key={p.label}>
            <rect x="16.5" y={y + 0.5} width="327" height="24" rx="6" fill={high ? C.red : C.panel2} fillOpacity={high ? 0.07 : 1} stroke={high ? C.red : C.line} strokeOpacity={high ? 0.35 : 1} />
            <circle cx="30" cy={y + 12.5} r="3.2" fill={col} />
            <text x="42" y={y + 16.5} fontSize="12" fill={C.text}>
              {txv(p.label)}
            </text>
            <text x="332" y={y + 16} fontSize="9.5" fontWeight="700" fill={col} textAnchor="end" letterSpacing="0.5">
              {txv(p.level)}
            </text>
          </g>
        );
      })}

      <rect x="16.5" y="259.5" width="92" height="28" rx="8" fill="none" stroke={C.mint} strokeOpacity="0.6" />
      <text x="62.5" y="277.5" fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Report it")}
      </text>
      <rect x="118.5" y="259.5" width="92" height="28" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="164.5" y="277.5" fontSize="11.5" fontWeight="600" fill={C.soft} textAnchor="middle">
        {tx("Cancel")}
      </text>
      <rect className="sr-anim-ring" x="220.5" y="259.5" width="123" height="28" rx="8" fill="none" stroke={C.red} strokeWidth="1.5" />
      <rect x="220.5" y="259.5" width="123" height="28" rx="8" fill={C.red} fillOpacity="0.16" stroke={C.red} strokeOpacity="0.6" />
      <text x="282" y="277.5" fontSize="11.5" fontWeight="700" fill={C.red} textAnchor="middle">
        {tx("Accept")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: grid of icon chips with a staggered gentle pulse        */
/* ------------------------------------------------------------------ */
type Glyph = "shadow" | "ai" | "code" | "exposed" | "consent" | "scope" | "approved";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "shadow":
      return (
        <g {...s}>
          <rect x="-8" y="-7" width="16" height="14" rx="2.5" strokeDasharray="3 2.4" />
          <line x1="-3" y1="0" x2="3" y2="0" />
        </g>
      );
    case "ai":
      return (
        <g {...s}>
          <rect x="-6" y="-6" width="12" height="12" rx="2.5" />
          <line x1="-3" y1="-9" x2="-3" y2="-6" />
          <line x1="3" y1="-9" x2="3" y2="-6" />
          <line x1="-3" y1="6" x2="-3" y2="9" />
          <line x1="3" y1="6" x2="3" y2="9" />
          <circle cx="0" cy="0" r="1.6" />
        </g>
      );
    case "code":
      return (
        <g {...s}>
          <polyline points="-4.5,-4.5 -9,0 -4.5,4.5" />
          <polyline points="4.5,-4.5 9,0 4.5,4.5" />
          <line x1="1.6" y1="-6" x2="-1.6" y2="6" />
        </g>
      );
    case "exposed":
      return (
        <g {...s}>
          <rect x="-6.5" y="-1.5" width="13" height="9" rx="2" />
          <path d="M-3.5 -1.5 V-4.5 a3.5 3.5 0 0 1 7 -1.5" />
        </g>
      );
    case "consent":
      return (
        <g {...s}>
          <rect x="-7" y="-8" width="14" height="16" rx="2" />
          <polyline points="-3.5,0 -1,2.6 3.5,-3" />
        </g>
      );
    case "scope":
      return (
        <g {...s}>
          <line x1="-8" y1="-4.5" x2="8" y2="-4.5" />
          <line x1="-8" y1="4.5" x2="8" y2="4.5" />
          <circle cx="-2.5" cy="-4.5" r="2" fill={C.panel} />
          <circle cx="3.5" cy="4.5" r="2" fill={C.panel} />
        </g>
      );
    case "approved":
      return (
        <g {...s}>
          <path d="M0 -8.5 L7.5 -5.5 V0.5 C7.5 5 4 7.5 0 9 C-4 7.5 -7.5 5 -7.5 0.5 V-5.5 Z" />
          <polyline points="-3,0.5 -0.8,2.8 3.2,-2" />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  sub: string;
}

const CHIPS: ChipSpec[] = [
  { g: "shadow", title: "Shadow IT", sub: "unapproved tools" },
  { g: "ai", title: "Shadow AI", sub: "unapproved AI tools" },
  { g: "code", title: "Vibe coding", sub: "apps built by prompt" },
  { g: "exposed", title: "Data exposure", sub: "readable by outsiders" },
  { g: "consent", title: "Consent screen", sub: "what an app may do" },
  { g: "scope", title: "Permission", sub: "mail, files, send" },
  { g: "approved", title: "Approved tool", sub: "agreement and checks in place" },
];

function TermsArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label={tx("Terms at a glance")} aria={tx("Seven terms shown as labelled icon chips: shadow IT, shadow AI, vibe coding, data exposure, consent screen, permission and approved tool")} height={H}>
      {CHIPS.map((c, i) => {
        const last = i === CHIPS.length - 1;
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = last ? 16 : 16 + col * 172;
        const y = 38 + row * 64;
        const w = last ? 328 : 156;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width={w - 1} height="51" rx="10" fill={C.panel2} stroke={C.line} />
            <circle className="sr-anim-pulse" cx="26" cy="26" r="16" fill={C.mint} fillOpacity="0.12" style={{ animationDelay: `${i * 0.35}s` }} />
            <g transform="translate(26 26)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="23" fontSize="12.5" fontWeight="700" fill={C.text}>
              {txv(c.title)}
            </text>
            <text x="51" y="39" fontSize="10.5" fill={C.muted}>
              {txv(c.sub)}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const ONE_CLICK_TOO_MANY_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "A helpful app can still be the leak.",
    illustration: LeakArt,
    caption: "Helpful is not the same as approved.",
    sections: [
      {
        h: "What this is about",
        body: "A busy day before a company expo. You need a demo fast, so you try an AI tool that builds dashboards, and an app that asks to connect to your work account. You decide what data goes in, what gets published, and what an app is allowed to do.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who uses apps, AI tools or shared files at work. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Nothing here looks like an attack. The tool is useful, the sign-in page is genuine, and one click is all it takes. A filter can\u2019t tell a well-meant shortcut from a risky one. You can.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Two ways one click exposes company data.",
    illustration: ConsentArt,
    caption: "Read what it asks for before you accept.",
    sections: [
      {
        h: "Unapproved tools see your data",
        body: "Uploading real customer data to a tool the company hasn\u2019t approved is a transfer to a service with no agreement. Nothing has to be public for that to be a problem.",
      },
      {
        h: "Published means readable",
        body: "A dashboard with customer records built into it, shared by public link, lets anyone with the link read them. Check what is inside before you choose who can see it.",
      },
      {
        h: "A real sign-in page doesn\u2019t vouch for the app",
        body: "An app can use a genuine consent screen. The page being real says nothing about who made the app or what it will do once you accept.",
      },
      {
        h: "Broad permissions are the warning",
        body: "An app that asks to read your mail and files, or send mail as you, can act with your access without ever knowing your password. If you doubt it, cancel and report it.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Shadow IT", body: "Apps or services used for work without the company\u2019s approval or knowledge." },
      { h: "Shadow AI", body: "The same thing with AI tools: you paste or upload company data into one nobody has approved." },
      { h: "Vibe coding", body: "Building an app or dashboard by describing it to an AI, often without reviewing what it produces or where the data goes." },
      { h: "Data exposure", body: "Data that people who shouldn\u2019t see it can read, by accident or by design." },
      { h: "Consent screen", body: "The page where you decide whether an app may act on your account. Accepting is a decision you make." },
      { h: "Permission", body: "One thing an app may do for you, such as read mail, read files or send mail as you. More permissions means more risk." },
      { h: "Approved tool", body: "A tool the company has checked and has an agreement with. If you aren\u2019t sure, ask before you use it." },
    ],
  },
];
